"use client";

import { useState, useMemo } from "react";
import { ChevronDownIcon } from "@/components/icons";
import { Checkbox } from "@/components/atoms";
import { PopoverPortal } from "@/components/molecules/Popover";
import classNames from "classnames";

export interface IMetricDef {
    name: string;
    dataKey: string;
    color: string;
    hidden?: boolean;
}

interface Props {
    metricSelectorLabel?: string;
    metrics: IMetricDef[];
    hiddenMetrics: Set<string>;
    onChangeVisibility: (metricName: string) => void;
    disabled?: boolean;
}

export const Metrics = ({ metrics, hiddenMetrics, onChangeVisibility, metricSelectorLabel = 'Metrics', disabled = false }: Props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const visibleCount = metrics.length - hiddenMetrics.size;
    const allSelected = hiddenMetrics.size === 0;
    const selectedMetrics = metrics.filter(m => !hiddenMetrics.has(m.name));
    
    const displayText = useMemo(() => {
        if (visibleCount === 0) return "None";
        if (allSelected) return "All";
        if (visibleCount === 1) return selectedMetrics[0]?.name || "1 Selected";
        return `${visibleCount} Selected`;
    }, [visibleCount, allSelected, selectedMetrics]);

    const filteredMetrics = useMemo(() => {
        if (!searchQuery.trim()) return metrics;
        return metrics.filter(m => 
            m.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [metrics, searchQuery]);

    const handleSelectAll = () => {
        if (allSelected) {
            metrics.forEach(m => {
                if (!hiddenMetrics.has(m.name)) onChangeVisibility(m.name);
            });
        } else {
            hiddenMetrics.forEach(name => onChangeVisibility(name));
        }
    };

    if (disabled) {
        return (
            <div className="flex items-center gap-2">
                <button
                    disabled
                    className="flex items-center gap-1 px-3 py-[5px] text-sm border border-gray-200 rounded-md bg-gray-50 opacity-50 cursor-not-allowed"
                >
                    <span className="font-bold font-roboto">{metricSelectorLabel}:</span>
                    <span className="font-normal font-roboto">All</span>
                    <ChevronDownIcon className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <PopoverPortal
                align="left"
                trigger={
                    <button
                        className={classNames(
                            "flex items-center gap-1 px-3 py-[5px] text-sm border rounded-md bg-white hover:bg-gray-50",
                            { "border-[#429795]": isOpen, "border-gray-300": !isOpen }
                        )}
                    >
                        <span className="font-bold font-roboto">{metricSelectorLabel}:</span>
                        <span className="font-normal font-roboto">{displayText}</span>
                        <ChevronDownIcon className={classNames("w-4 h-4 transition-transform", { "rotate-180": isOpen })} />
                    </button>
                }
                onOpenChange={setIsOpen}
                className="overflow-hidden"
            >
                <div className="w-52 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                        <div className="relative">
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                            >
                                <path d="m21 21-4.34-4.34"></path>
                                <circle cx="11" cy="11" r="8"></circle>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search ..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#079a95] focus:ring-1 focus:ring-[#079a95] font-roboto"
                            />
                        </div>
                    </div>
                    <div className="p-1">
                        <div
                            className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50 cursor-pointer"
                            onClick={handleSelectAll}
                        >
                            <Checkbox checked={allSelected} />
                            <span className="text-sm text-gray-700 font-medium font-roboto">Select All</span>
                        </div>
                        <div className="border-b border-gray-200 my-1" />
                        {filteredMetrics.map((metric, index) => {
                            const isHidden = hiddenMetrics.has(metric.name);
                            return (
                                <div
                                    key={`${metric.dataKey}-${index}`}
                                    className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50 cursor-pointer"
                                    onClick={() => onChangeVisibility(metric.name)}
                                >
                                    <Checkbox checked={!isHidden} />
                                    <span className="text-sm text-gray-700 font-roboto">{metric.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </PopoverPortal>
        </div>
    );
};
